<script setup>
// Import components
import DialogBar from 'components/DialogBar.vue'
import DeleteDependencies from 'components/UI/Dialogs/DeleteDependencies.vue'
import DialogFooter from 'components/UI/Dialogs/DialogFooter.vue'
import { format } from 'quasar'
import { getDeleteDependencies } from 'src/utils/deleteDependencies'

// Quasar Dialog
defineEmits([...useDialogPluginComponent.emits])
const { dialogRef, onDialogHide, onDialogOK, onDialogCancel } = useDialogPluginComponent()

// Constants declarations
const { capitalize } = format
const $q = useQuasar()
const attrs = useAttrs()
const { t, te } = useI18n({ useScope: 'global' })
const api = useFeathers()

// Component props
const componentProps = defineProps({
  dialogIcon: { type: String, default: null },
  dialogSubtitle: { type: String, default: null },
  propItem: {
    type: Object,
    required: true,
  },
  onError: {
    type: Function,
    default: null,
  },
  onSuccess: {
    type: Function,
    default: null,
  },
  itemsService: {
    type: String,
    required: true,
    default: null,
  },
  type: {
    type: String,
    required: false,
    default: 'delete',
  },
  customProps: {
    type: Object,
    required: false,
    default: null,
  },
  itemName: {
    type: String,
    required: false,
    default: null,
  },
  onSuccessData: {
    type: Object,
    required: false,
    default: null,
  },
  deleteOnService: {
    type: Boolean,
    required: false,
    default: false,
  },
})

// Refs
const maximizedToggle = ref(false)

// Computed properties
const maximized = computed(() => {
  return maximizedToggle.value || $q.screen.lt.md
})
const dialogTranslationKey = computed(() => componentProps.customProps?.dialogTranslationKey || null)
const deleteItemTextTranslationKey = computed(() => {
  if (componentProps.customProps?.deleteItemTextTranslationKey) {
    return componentProps.customProps.deleteItemTextTranslationKey
  }

  return te(process.env.FRONTEND_TYPE + '.' + capitalize(componentProps.itemsService) + '.Delete item text')
    ? process.env.FRONTEND_TYPE + '.' + capitalize(componentProps.itemsService) + '.Delete item text'
    : 'Shared.' + capitalize(componentProps.itemsService) + '.Delete item text'
})

// Store(s)
const authStore = useAuthStore()

// Refs
const loading = ref(false)
const dependencies = ref([])
const dependencyMessage = ref(null)
const archive = ref(false)
const forceRemove = ref(false)
const confirmCheckmark = ref(false)
const showArchive = ref(componentProps.customProps?.showArchive === true)
const showForceRemove = ref(componentProps.customProps?.showForceRemove === true)
const showConfirmCheckmark = ref(componentProps.customProps?.confirmCheckmark === true)

// Methods
const handleFormError = (error) => {
  dependencies.value = getDeleteDependencies(error)
  dependencyMessage.value = null
  let processGenericFormErrors = true
  const errorEntries = Array.isArray(error.data) ? error.data : []

  if (errorEntries.includes('RESTRICT_DELETE')) {
    showArchive.value = false
    archive.value = false
    showForceRemove.value = false
    forceRemove.value = false
    processGenericFormErrors = false
    if (process.env.FRONTEND_TYPE === 'Customer' && te('Customer.' + capitalize(componentProps.itemsService) + '.Errors.Delete item has dependencies')) {
      $q.notify({
        type: 'negative',
        progress: true,
        message: t('Customer.' + capitalize(componentProps.itemsService) + '.Errors.Delete item has dependencies'),
      })
    } else {
      $q.notify({
        type: 'negative',
        progress: true,
        message: t('Shared.Globals.Errors.Delete item has dependencies'),
      })
    }
  } else if (errorEntries.includes('RESTRICT_DELETE_FORCABLE')) {
    processGenericFormErrors = false
    $q.notify({
      type: 'negative',
      progress: true,
      message: t('Shared.Globals.Errors.Delete item has dependencies or force delete'),
    })

    showForceRemove.value = true
  } else if (errorEntries.includes('RESTRICT_DELETE_ARCHIVABLE')) {
    processGenericFormErrors = false
    if (!componentProps.propItem.isArchived) {
      $q.notify({
        type: 'negative',
        progress: true,
        message: t('Shared.Globals.Errors.Delete item has dependencies or archive'),
      })
      showArchive.value = true
    } else {
      $q.notify({
        type: 'negative',
        progress: true,
        message: t('Shared.Globals.Errors.Delete item has dependencies'),
      })
    }
  }

  if (errorEntries.length > 0) {
    for (const errorEntry of errorEntries) {
      if (['RESTRICT_DELETE', 'RESTRICT_DELETE_ARCHIVABLE', 'RESTRICT_DELETE_FORCABLE'].includes(errorEntry)) {
        continue
      }

      if (typeof errorEntry === 'string') {
        const errorMessageKey = process.env.FRONTEND_TYPE + '.' + capitalize(componentProps.itemsService) + '.Errors.' + errorEntry
        if (te(errorMessageKey)) {
          processGenericFormErrors = false
          dependencyMessage.value = t(errorMessageKey)
          $q.notify({
            type: 'negative',
            progress: true,
            message: t(errorMessageKey),
          })
        }
      }
    }
  }

  if (processGenericFormErrors === true) {
    componentProps.onError?.(
      {
        type: componentProps.type || attrs.type,
        message: error?.message,
        errors: error?.errors || error?.data?.errors,
      },
      componentProps.itemsService,
    )
  }
}

const onSubmit = async () => {
  dependencies.value = []
  dependencyMessage.value = null
  loading.value = true
  try {
    let response

    if (componentProps.deleteOnService) {
      response = await api
        .service(componentProps.itemsService)
        .remove(componentProps.propItem._id, {
          query: {
            showArchived: componentProps.customProps?.showArchived !== true ? undefined : componentProps.customProps.showArchived,
            forceRemove: showForceRemove.value !== true ? undefined : forceRemove.value,
            forceArchive: showArchive.value !== true ? undefined : archive.value,
          },
        })
        .catch((err) => {
          handleFormError(err)
          return false
        })
    } else {
      response = await componentProps.propItem
        .remove({
          query: {
            showArchived: componentProps.customProps?.showArchived !== true ? undefined : componentProps.customProps.showArchived,
            forceRemove: showForceRemove.value !== true ? undefined : forceRemove.value,
            forceArchive: showArchive.value !== true ? undefined : archive.value,
          },
        })
        .catch((err) => {
          handleFormError(err)
          return false
        })
    }

    if (response?._id) {
      if (componentProps.onSuccessData) {
        componentProps.onSuccess(componentProps.onSuccessData.payload, componentProps.onSuccessData.itemsService, componentProps.onSuccessData.type)
      } else {
        componentProps.onSuccess(response, componentProps.itemsService, componentProps.type || attrs.type)
      }
      onDialogOK(response)
    }
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <q-dialog ref="dialogRef" persistent @hide="onDialogHide" :maximized="$q.screen.lt.md">
    <q-card class="relative-position" :class="!maximized ? ($frontendType === 'Customer' ? 'dialog-width-fixed-800' : 'dialog-width-33') : ''">
      <dialog-bar
        :icon="dialogIcon || 'sym_o_delete'"
        :subtitle="dialogSubtitle"
        v-model:maximized-toggler="maximizedToggle"
        :maximized="maximized"
        :dialog-type="type"
        :items-service="itemsService"
        :translation-key="dialogTranslationKey"
        :close-btn-disabled="loading"
      />
      <q-separator />
      <q-card-section :class="!maximized ? 'dialog-normal' : 'dialog-maximized'">
        <i18n-t :keypath="deleteItemTextTranslationKey" tag="p" scope="global">
          <strong>{{ itemName ? itemName : propItem.name || propItem.fullName }}</strong>
          <strong>
            <template v-if="!showArchive"
              >{{
                $te($frontendType + '.' + capitalize(itemsService) + '.Delete')
                  ? $t($frontendType + '.' + capitalize(itemsService) + '.Delete')
                  : $t('Shared.' + capitalize(itemsService) + '.Delete')
              }}
            </template>
            <template v-else
              >{{
                $te($frontendType + '.' + capitalize(itemsService) + '.Archive')
                  ? $t($frontendType + '.' + capitalize(itemsService) + '.Archive')
                  : $t('Shared.' + capitalize(itemsService) + '.Archive')
              }}
            </template>
          </strong>
        </i18n-t>

        <!-- Dependency summary -->
        <DeleteDependencies :dependencies="dependencies" :message="dependencyMessage" />

        <q-checkbox v-if="showConfirmCheckmark" v-model="confirmCheckmark" class="q-pa-0 q-ma-0">
          <i18n-t
            :keypath="
              $te($frontendType + '.' + capitalize(itemsService) + '.Confirm delete')
                ? $frontendType + '.' + capitalize(itemsService) + '.Confirm delete'
                : 'Shared.' + capitalize(itemsService) + '.Confirm delete'
            "
            tag="span"
            scope="global"
          >
            <strong>{{ itemName ? itemName : propItem.name || propItem.fullName }}</strong>
          </i18n-t>
        </q-checkbox>

        <q-checkbox v-if="showForceRemove" v-model="forceRemove" class="q-pa-0 q-ma-0">
          <i18n-t :keypath="'Shared.Globals.Delete item force delete'" tag="span" scope="global">
            <strong>{{ itemName ? itemName : propItem.name || propItem.fullName }}</strong>
          </i18n-t>
        </q-checkbox>

        <q-checkbox v-if="showArchive" v-model="archive" class="q-pa-0 q-ma-0">
          <i18n-t :keypath="'Shared.Globals.Delete item archive'" tag="span" scope="global">
            <strong>{{ itemName ? itemName : propItem.name || propItem.fullName }}</strong>
          </i18n-t>
        </q-checkbox>
      </q-card-section>
      <q-card-actions align="right">
        <DialogFooter>
          <template #footer-buttons>
            <div class="col q-pr-xs items-end">
              <q-btn
                v-if="authStore.hasServicePermission(itemsService, ['remove_all', 'remove_assigned'])"
                color="negative"
                :label="$t('Shared.Globals.Delete')"
                :loading="loading"
                :disable="loading || (showForceRemove && !forceRemove) || (showArchive && !archive) || (showConfirmCheckmark && !confirmCheckmark)"
                class="btn-min-width-100 q-mr-sm"
                @click="onSubmit"
              />
            </div>
            <div class="col column items-end">
              <q-btn :disable="loading" color="primary" :label="$t('Shared.Globals.Cancel')" class="btn-min-width-100" @click="onDialogCancel" />
            </div>
          </template>
        </DialogFooter>
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<style scoped></style>
